import React, {useMemo, useState} from 'react'
import {useColor, useTag} from '../../../utils/etc'
import {useDisplayNumber} from '../../../utils/number'
import ContainerLayout from '../../../components/layout/ContainerLayout'
import TableHeader from '../../../components/table/TableHeader'
import Text from '../../../components/text/Text'
import Span from '../../../components/text/Span'
import TableItem from '../../../components/table/TableItem'
import WizImage from '../../../components/image/WizImage'

import {IPosition} from '../../../apis/types/portfolio'

interface IProps {
    className?: string
    data?: IPosition[]
}

const PositionContainer: React.FC<IProps> = ({className, data}) => {
    const {renderLeverageColor, renderProfitArrow} = useTag()
    const {getNumberColor} = useColor()
    const {renderPercent, floorBalance} = useDisplayNumber()

    const [showAll, setShowAll] = useState(false)
    const positions = useMemo(() => {
        if (data?.length === 0) {
            return []
        }
        return data
    }, [data])

    return (
        <ContainerLayout title={'Positions'} className={`shadow-none  sm:text-center sm:space-y-[20px] ${className}`}>
            <TableHeader className={'grid-cols-[auto_150px_140px] lg:grid-cols-[1fr_120px_120px_140px] md:hidden'}>
                <Text>Symbol</Text>
                <Text className={'hidden lg:block'}>Buy Price</Text>
                <div>
                    <Text className={'lg:hidden'}>Buy Price</Text>
                    <Text>Current Price</Text>
                </div>
                <Text>
                    Profit<Span className={'text-body3 text-font03'}>(Amount)</Span>
                </Text>
            </TableHeader>

            {positions?.map(item => (
                <TableItem
                    key={item?.key_id}
                    className={
                        'grid-cols-[auto_150px_140px] lg:grid-cols-[1fr_120px_120px_140px] md:grid-cols-3 md:gap-y-[20px] md:items-start md:px-0 sm:grid-cols-1'
                    }>
                    <div
                        className={
                            'flex space-x-[10px] items-center md:col-span-3 md:justify-center md:flex-col md:gap-y-[5px] sm:col-span-1'
                        }>
                        {item?.logo && (
                            <WizImage src={item?.logo} className={'w-[24px] h-[24px] rounded-full flex-none'} />
                        )}
                        <div>
                            <Text className={'md:text-center md:text-ti2'}>{item?.symbol}</Text>
                            <div className={'space-x-[5px] flex items-center md:mt-[5px]'}>
                                <Text
                                    className={`text-ti4 px-[5px] py-[3px] text-center rounded-[5px] ${renderLeverageColor(
                                        item?.side,
                                    )}`}>
                                    {Math.floor(item?.leverage)}x {item?.side === 'long' ? 'L' : 'S'}
                                </Text>
                                <Text className={`text-ti2 ${getNumberColor(item?.profit)}`}>
                                    {renderPercent(item?.profit)}
                                </Text>
                            </div>
                        </div>
                    </div>
                    <div className={'hidden lg:block md:space-y-[5px]'}>
                        <Text className={'hidden md:block text-ti3 text-font02 text-center'}>Buy Price</Text>
                        <Text className={'text-font03 md:text-center'}>
                            {floorBalance(item?.avg_buy_price, 10000)}{' '}
                        </Text>
                    </div>
                    <div className={'md:space-y-[5px]'}>
                        <Text className={'hidden md:block text-ti3 text-font02 text-center'}>Current Price</Text>
                        <Text className={'text-font03 lg:hidden'}>{floorBalance(item?.avg_buy_price, 10000)} </Text>
                        <Text className={'text-ti2 md:text-center'}>{floorBalance(item?.current_price, 10000)}</Text>
                    </div>
                    <div className={'md:space-y-[5px]'}>
                        <Text className={'hidden md:block text-ti3 text-font02 text-center'}>
                            Profit<Span className={'text-body_comm text-font03'}>(Amount)</Span>
                        </Text>
                        <div className={'flex items-center space-x-[3px] md:justify-center'}>
                            <Text className={`text-ti2 ${getNumberColor(item?.unrealised_pnl)}`}>
                                {floorBalance(item?.unrealised_pnl)}
                            </Text>
                            <WizImage
                                src={renderProfitArrow(item?.pnl || item?.unrealised_pnl)}
                                className={'w-[16px] flex-none'}
                            />
                        </div>
                        <Text className={'text-body3 text-font03 md:text-center'}>
                            ({item?.amount.toLocaleString()})
                        </Text>
                    </div>
                </TableItem>
            ))}

            {positions?.length === 0 && (
                <TableItem className={'text-center'}>
                    <div>
                        <Text>No Items</Text>
                    </div>
                </TableItem>
            )}
        </ContainerLayout>
    )
}

export default PositionContainer
